import type { CustomerStateData } from "~/gql/graphqlBase.generated";
import type { CustomerFormState } from "~fulfill/customers/customer/components/CustomerForm";
import type { SavedBaselineState } from "~fulfill/sales/pricing/hooks/useSavedBaseline";

import isRecord from "~/helpers/isRecord";
import toCustomerFormState from "~fulfill/customers/customer/helpers/customerFormState";
import { haveDisabledVolumePoolsChanged } from "~fulfill/customers/customer/helpers/disabledVolumePools";
import salesPeopleEmailsOf from "~fulfill/customers/customer/helpers/salesPeopleEmails";

export interface CustomerDraft {
	billingPermission: boolean;
	disabledVolumePoolIds: string[];
	form: CustomerFormState;
	locationPermission: boolean;
	salesPeopleEmails: string[];
}

export const customerDraftFromSaved = (customer: CustomerStateData): CustomerDraft => ({
	billingPermission: customer.allowOrderBillingAddressEdit ?? true,
	disabledVolumePoolIds: customer.disabledVolumePoolIds ?? [],
	form: toCustomerFormState(customer),
	locationPermission: customer.allowOrderLocationAddressEdit ?? true,
	salesPeopleEmails: salesPeopleEmailsOf(customer)
});

const sameFieldValue = (from: unknown, to: unknown): boolean => (from ?? "") === (to ?? "");

export const hasCustomerDraftChanged = (from: CustomerDraft, to: CustomerDraft): boolean => {
	const fields = Object.keys({ ...from.form, ...to.form }) as (keyof CustomerFormState)[];
	return (
		fields.some((field) => !sameFieldValue(from.form[field], to.form[field])) ||
		from.salesPeopleEmails.join("\n") !== to.salesPeopleEmails.join("\n") ||
		haveDisabledVolumePoolsChanged(to.disabledVolumePoolIds, from.disabledVolumePoolIds) ||
		from.billingPermission !== to.billingPermission ||
		from.locationPermission !== to.locationPermission
	);
};

export const isCustomerDraft = (value: unknown): value is CustomerDraft =>
	isRecord(value) &&
	isRecord(value.form) &&
	typeof value.form.customerId === "string" &&
	Array.isArray(value.salesPeopleEmails) &&
	Array.isArray(value.disabledVolumePoolIds) &&
	typeof value.billingPermission === "boolean" &&
	typeof value.locationPermission === "boolean";

const followSavedDraft = (
	current: SavedBaselineState<CustomerDraft>,
	saved: CustomerDraft
): SavedBaselineState<CustomerDraft> =>
	hasCustomerDraftChanged(current.baseline, current.draft) ? current : { baseline: saved, draft: saved };

export default followSavedDraft;
